import merge from 'lodash-es/merge';
import useDefaultChartOptions from '@/components/charts/useDefaultChartOptions';
import useLineChartOptions from '@/components/charts/useLineChartOptions';
import { CourseStatistics, Region } from '@/types';

export const getAccumulatedDevelopmentConfig = (courseStatistics: CourseStatistics[], fromDate: Date, toDate: Date, partnerRegionsSelected: boolean, selectedRegions?: Region[]) => {
    const allDates = getDatesBetween(fromDate, toDate);

    const series = [];
    if (partnerRegionsSelected) {
        const partnerRegionsSeries = getSeriesForCourseStatistics(courseStatistics, allDates);
        series.push({
            name: 'Partnerkommuner',
            data: partnerRegionsSeries,
            boostThreshold: 1,
        });
    } else if (!selectedRegions?.length) {
        const allRegionsSeries = getSeriesForCourseStatistics(courseStatistics, allDates);
        series.push({
            name: 'Alle kommuner',
            data: allRegionsSeries,
            boostThreshold: 1,
        });
    } else {
        const selectedRegionsSeries = selectedRegions.map((region) => {
            const regionSeries = getSeriesForCourseStatistics(
                courseStatistics.filter((courseStatistic) => courseStatistic.region === region.id),
                allDates
            );
            return {
                name: region.title,
                data: regionSeries,
                boostThreshold: 1,
            };
        });
        series.push(...selectedRegionsSeries);
    }
    return {
        printTitle: 'Akkumuleret tilvækst i startede forløb',
        options: merge(useDefaultChartOptions(), useLineChartOptions(), {
            title: { text: 'Akkumuleret tilvækst i startede forløb' },
            boost: {
                useGPUTranslations: true,
                seriesThreshold: 5,
                enabled: true,
            },
            chart: {
                animation: false,
                shadow: false,
            },
            xAxis: {
                type: 'datetime',
            },
            plotOptions: {
                animation: false,
                shadow: false,
                marker: {
                    enabled: false,
                },
                enableMouseTracking: false,
                series: {
                    borderRadius: 5,
                    tooltip: {
                        headerFormat: '',
                        pointFormatter() {
                            const formattedDate = new Intl.DateTimeFormat('da-DK', { year: 'numeric', month: 'long', day: 'numeric' }).format((this as any).x);
                            return `<span style="font-size: 10px">${formattedDate}</span><br/><span style="color:${(this as any).series.color}">●</span> ${(this as any).series.name}: <b>${(this as any).y}</b>`;
                        },
                    },
                },
            },
            yAxis: {
                title: {
                    text: 'Antal forløb',
                },
            },
            series,
        }),
    };
};

function getSeriesForCourseStatistics(courseStatistics: CourseStatistics[], allDates: Date[]) {
    const allRegionsSeriesGroupedByDate = courseStatistics.reduce((acc, courseStatistic) => {
        const date = new Date(courseStatistic.started);
        const dateKey = `${date.getFullYear()}/${date.getMonth() + 1}/${date.getDate()}`;

        acc[dateKey] = acc[dateKey] || 0;
        acc[dateKey] += 1;

        return acc;
    }, {} as { [key: string]: number });

    allDates.forEach((date) => {
        const dateKey = `${date.getFullYear()}/${date.getMonth() + 1}/${date.getDate()}`;
        if (!allRegionsSeriesGroupedByDate[dateKey]) {
            allRegionsSeriesGroupedByDate[dateKey] = 0;
        }
    });

    return Object.entries(allRegionsSeriesGroupedByDate)
        .sort((a, b) => {
            return new Date(a[0]).getTime() - new Date(b[0]).getTime();
        })
        .reduce((acc, [date, count]) => {
            const lastValue = acc[acc.length - 1]?.y || 0;
            acc.push({
                x: new Date(date),
                y: lastValue + count,
            });
            return acc;
        }, [] as { x: Date; y: number }[]);
}

const getDatesBetween = (startDate: Date, endDate: Date) => {
    const dates = [];
    const currentDate = startDate;

    while (currentDate <= endDate) {
        dates.push(new Date(currentDate));
        currentDate.setDate(currentDate.getDate() + 1);
    }

    return dates;
};
